import React from "react";
import { Navigate, useParams } from "react-router-dom";
import { STORAGE_KEY } from "../features/api/apiSlice";

/**
 * Sends a signed-out visitor to a route's public equivalent instead of the
 * login page.
 *
 * Some routes are reached from outside the portal: the QR printed on an event
 * access card lands on /scan/{barcode}, and whoever scanned it may have no
 * account at all. A password box is not an answer to what they asked, so those
 * routes name a public read-only view of the same thing and this sends guests
 * there.
 *
 * It has to sit OUTSIDE OnboardingGuard, not inside it or inside
 * RequirePermission. OnboardingGuard is the first thing a private route renders
 * and it navigates a session-less visitor straight to /login — and worse, calls
 * /me on the way, which 401s and triggers the api slice's own hard redirect.
 * Neither inner guard ever gets a say.
 *
 * `to` is a function of the route params so the public view can be pointed at
 * the same record; a route without one renders exactly as before.
 */
export default function GuestRedirect({
  to,
  children,
}: {
  to?: (params: Record<string, string | undefined>) => string;
  children: React.ReactNode;
}) {
  const params = useParams();

  if (!to) return <>{children}</>;

  let token: string | null = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    token = raw ? JSON.parse(raw)?.token ?? null : null;
  } catch {
    // Unreadable or corrupt auth blob counts as signed out.
    token = null;
  }

  if (token) return <>{children}</>;

  return <Navigate to={to(params)} replace />;
}
